// src/utils/orgAreaConfig.js
//
// Pass 82E — organization operating-area / service-location / meeting-point CONFIGURATION (flat org-scoped top-level
// collections, mirroring `contentPacks`/`bookings`). Read helpers are open to org members; writers are gated to org
// ADMIN (owner/admin) and write only canonical whitelisted fields. Meeting points are NEVER fabricated — operators
// create them; this module only stores/normalizes what they enter. Reused by the resolver context so Operations reads
// and the area filter agree with one source of config truth.

import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db as defaultDb } from '../firebase';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { canManageOrganization } from './platformAccess';
import { sourceOperatingAreaFor } from '../config/operationalSources';
import {
  AREA_STATUS, LOCATION_STATUS, operatingAreaDocId, serviceLocationDocId, meetingPointDocId,
  areaKeyOf, leafKeyOf, isDescendantId, AREA_WIDE_LOCATION_KEY,
} from './operatingAreas';

export const OPERATING_AREAS = 'operatingAreas';
export const SERVICE_LOCATIONS = 'serviceLocations';
export const MEETING_POINTS = 'meetingPoints';

const s = (v) => String(v == null ? '' : v).trim();
const arr = (v) => (Array.isArray(v) ? v.map(s).filter(Boolean) : []);

// ── Normalizers (read-model shape; identity fields immutable once written) ───────────────────────────────────────────
export function normalizeOperatingArea(r = {}) {
  return {
    id: s(r.id || r.operatingAreaId),
    operatingAreaId: s(r.id || r.operatingAreaId),
    organizationId: s(r.organizationId),
    name: s(r.name),
    status: [AREA_STATUS.ACTIVE, AREA_STATUS.PLANNED, AREA_STATUS.INACTIVE].includes(s(r.status)) ? s(r.status) : AREA_STATUS.PLANNED,
    timezone: s(r.timezone) || 'UTC',
    isDefault: r.isDefault === true,
    sortOrder: Number.isFinite(r.sortOrder) ? r.sortOrder : 0,
  };
}
export function normalizeServiceLocation(r = {}) {
  return {
    id: s(r.id || r.serviceLocationId),
    serviceLocationId: s(r.id || r.serviceLocationId),
    organizationId: s(r.organizationId),
    operatingAreaId: s(r.operatingAreaId),
    name: s(r.name),
    status: s(r.status) === LOCATION_STATUS.INACTIVE ? LOCATION_STATUS.INACTIVE : LOCATION_STATUS.ACTIVE,
    transportRequired: r.transportRequired === true,
    sortOrder: Number.isFinite(r.sortOrder) ? r.sortOrder : 0,
  };
}
export function normalizeMeetingPoint(r = {}) {
  return {
    id: s(r.id || r.meetingPointId),
    meetingPointId: s(r.id || r.meetingPointId),
    organizationId: s(r.organizationId),
    operatingAreaId: s(r.operatingAreaId),
    serviceLocationId: s(r.serviceLocationId),
    name: s(r.name),
    address: s(r.address),
    mapUrl: s(r.mapUrl),
    status: s(r.status) === LOCATION_STATUS.INACTIVE ? LOCATION_STATUS.INACTIVE : LOCATION_STATUS.ACTIVE,
    aliases: arr(r.aliases),
  };
}

// ── Reads (org-scoped) ───────────────────────────────────────────────────────────────────────────────────────────────
const byOrg = (db, collectionName, organizationId) => loadRecordsByEquality(db, { collectionName, field: 'organizationId', value: organizationId });

export async function loadOrgAreaConfig(db = defaultDb, organizationId = '') {
  if (!organizationId) return { areas: [], serviceLocations: [], meetingPoints: [] };
  const [areas, serviceLocations, meetingPoints] = await Promise.all([
    byOrg(db, OPERATING_AREAS, organizationId),
    byOrg(db, SERVICE_LOCATIONS, organizationId),
    byOrg(db, MEETING_POINTS, organizationId),
  ]);
  return {
    areas: areas.map(normalizeOperatingArea).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    serviceLocations: serviceLocations.map(normalizeServiceLocation).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    meetingPoints: meetingPoints.map(normalizeMeetingPoint).sort((a, b) => a.name.localeCompare(b.name)),
  };
}

// The org's default operating area id (org doc field wins; else a single active area; else ''). Used ONLY as the
// bounded legacy resolver default — never a silent default for a new record.
export function defaultOperatingAreaIdOf(org = {}, areas = []) {
  const declared = s(org && (org.defaultOperatingAreaId));
  if (declared) return declared;
  const active = areas.filter((a) => a.status === AREA_STATUS.ACTIVE);
  return active.length === 1 ? active[0].operatingAreaId : '';
}

// Build the ctx for resolveBookingOperatingArea from loaded config (one source of truth for reads + filter).
export function buildAreaResolverContext(org = {}, areas = []) {
  return {
    defaultOperatingAreaId: defaultOperatingAreaIdOf(org, areas),
    sourceAreaFor: (b) => {
      const areaKey = sourceOperatingAreaFor(b && b.organizationId, b && (b.externalSource || b.source));
      return areaKey ? operatingAreaDocId(b.organizationId, areaKey) : '';
    },
  };
}

// ── Writers (org ADMIN only; canonical field whitelists; org-namespaced ids) ─────────────────────────────────────────
function assertAdmin(userData, organizationId) {
  if (!canManageOrganization(userData, organizationId)) {
    const err = new Error('Only an organisation owner or admin can manage operating areas.');
    err.code = 'FORBIDDEN';
    throw err;
  }
}

export async function saveOperatingArea(db, { organizationId, key, name, status = AREA_STATUS.PLANNED, timezone = 'UTC', isDefault = false, sortOrder = 0 }, { userData } = {}) {
  assertAdmin(userData, organizationId);
  if (!organizationId || !s(key) || !s(name)) throw new Error('Area organisation, key and name are required.');
  const id = operatingAreaDocId(organizationId, key);
  await setDoc(doc(db, OPERATING_AREAS, id), {
    organizationId: s(organizationId), name: s(name),
    status: [AREA_STATUS.ACTIVE, AREA_STATUS.PLANNED, AREA_STATUS.INACTIVE].includes(status) ? status : AREA_STATUS.PLANNED,
    timezone: s(timezone) || 'UTC', isDefault: isDefault === true, sortOrder: Number(sortOrder) || 0,
    updatedAt: serverTimestamp(), createdAt: serverTimestamp(),
  }, { merge: true });
  return id;
}

export async function saveServiceLocation(db, { organizationId, key, operatingAreaId, name, status = LOCATION_STATUS.ACTIVE, transportRequired = false, sortOrder = 0 }, { userData } = {}) {
  assertAdmin(userData, organizationId);
  if (!organizationId || !s(key) || !s(operatingAreaId) || !s(name)) throw new Error('Service location organisation, key, area and name are required.');
  // Pass 82E.1 §12 — referential integrity: the parent area must belong to the SAME org (org-namespaced id prefix).
  if (!isDescendantId(s(organizationId), s(operatingAreaId))) throw new Error('Operating area does not belong to this organisation.');
  // Hierarchical id: <org>::<areaKey>::<locKey> — a same-named location in another area gets a DISTINCT id.
  const id = serviceLocationDocId(organizationId, areaKeyOf(operatingAreaId), key);
  await setDoc(doc(db, SERVICE_LOCATIONS, id), {
    organizationId: s(organizationId), operatingAreaId: s(operatingAreaId), name: s(name),
    status: status === LOCATION_STATUS.INACTIVE ? LOCATION_STATUS.INACTIVE : LOCATION_STATUS.ACTIVE,
    transportRequired: transportRequired === true, sortOrder: Number(sortOrder) || 0,
    updatedAt: serverTimestamp(), createdAt: serverTimestamp(),
  }, { merge: true });
  return id;
}

export async function saveMeetingPoint(db, { organizationId, key, operatingAreaId, serviceLocationId = '', name, address = '', mapUrl = '', status = LOCATION_STATUS.ACTIVE, aliases = [] }, { userData } = {}) {
  assertAdmin(userData, organizationId);
  if (!organizationId || !s(key) || !s(operatingAreaId) || !s(name)) throw new Error('Meeting point organisation, key, area and name are required.');
  // Pass 82E.1 §12 — referential integrity: parent area is same-org; if a service location is given it must be same-org
  // AND under the SAME operating area (no cross-area / cross-org dangling references, even if the caller knows an id).
  if (!isDescendantId(s(organizationId), s(operatingAreaId))) throw new Error('Operating area does not belong to this organisation.');
  if (s(serviceLocationId) && !isDescendantId(s(operatingAreaId), s(serviceLocationId))) throw new Error('Service location does not belong to this operating area.');
  // Hierarchical id: <org>::<areaKey>::<locKeyOrAreaWide>::<mpKey>. Area-wide (§9) when no service location is chosen.
  const locSlot = s(serviceLocationId) ? leafKeyOf(serviceLocationId) : AREA_WIDE_LOCATION_KEY;
  const id = meetingPointDocId(organizationId, areaKeyOf(operatingAreaId), locSlot, key);
  await setDoc(doc(db, MEETING_POINTS, id), {
    organizationId: s(organizationId), operatingAreaId: s(operatingAreaId), serviceLocationId: s(serviceLocationId), name: s(name),
    address: s(address), mapUrl: s(mapUrl),
    status: status === LOCATION_STATUS.INACTIVE ? LOCATION_STATUS.INACTIVE : LOCATION_STATUS.ACTIVE, aliases: arr(aliases),
    updatedAt: serverTimestamp(), createdAt: serverTimestamp(),
  }, { merge: true });
  return id;
}

// Archive rather than delete when referenced by history (§40/§41) — set status inactive, keep the id.
export async function setEntityStatus(db, collectionName, id, status, { userData, organizationId } = {}) {
  assertAdmin(userData, organizationId);
  await updateDoc(doc(db, collectionName, id), { status: s(status), updatedAt: serverTimestamp() });
}
